import React, { useEffect, useState } from "react";
import {
  Box,
  Grid,
  Typography,
  TextField,
  Button,
  IconButton,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Drawer,
  Divider,
  Autocomplete,
} from "@mui/material";
import "../../App.css";
import { useNavigate } from "react-router-dom";
import SearchIcon from "@mui/icons-material/Search";
import FilterListIcon from "@mui/icons-material/FilterList";
import VisibilityIcon from "@mui/icons-material/Visibility";
import EditIcon from "@mui/icons-material/Edit";
import CloseIcon from "@mui/icons-material/Close";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import FolderOpenOutlinedIcon from "@mui/icons-material/FolderOpenOutlined";
import PictureAsPdfOutlinedIcon from "@mui/icons-material/PictureAsPdfOutlined";
import { TableNodata } from "../../components/TableNodata";
import { TruncatedText } from "../../components/TruncatedText";
import { TableLoader } from "../../components/TableLoader";
import { OverAllLoader } from "../../components/OverAllLoader";
import CustomPagination from "../../components/CustomPagination";
import { useChangeableItems, getLabelByKey, getPluralLabelByKey } from "../../store/useDynamicLabel";
import api from "../../api/client";
import { useSnackbar } from "../../providers/SnackbarProvider";
import useDebounce from "../../hooks/useDebounce";
import { programLabel, subjectLabel, subjectId } from "./AddDocument";

const ROWS_PER_PAGE = 10;

// documents/view takes document_key; document_id is the fallback while the list API doesn't return the key
export const viewPayload = (item) => (item.document_key ? { document_key: item.document_key } : { document_id: item.document_id });

// Status badge colours — same badge as the Subject list status column
const STATUS_STYLES = {
  READY: { bg: "#d4edda", color: "#155724" },
  PROCESSED: { bg: "#d4edda", color: "#155724" },
  PROCESSING: { bg: "#fff3cd", color: "#856404" },
  PENDING: { bg: "#fff3cd", color: "#856404" },
  FAILED: { bg: "#f8d7da", color: "#721c24" },
};

// Used by the list Status column and the View page
export const StatusBadge = ({ status }) => (
  <Box
    sx={{
      display: "inline-block",
      px: 2,
      py: 0.5,
      borderRadius: "12px",
      fontWeight: "bold",
      fontSize: "12px",
      backgroundColor: STATUS_STYLES[status]?.bg || "#f1f1f1",
      color: STATUS_STYLES[status]?.color || "#333",
      textAlign: "center",
    }}
  >
    {status || "-"}
  </Box>
);

// Status filter options (values the list API returns)
const STATUSES = ["PROCESSING", "READY", "FAILED"];
const formatStatus = (value) => (value ? value.charAt(0) + value.slice(1).toLowerCase() : "");

// Code (blue, small) above name — same cell style as the Question Bank list
const CodeNameCell = ({ code, name }) => (
  <TableCell sx={{ maxWidth: "220px" }}>
    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.2 }}>
      {code && (
        <Typography sx={{ fontWeight: 700, fontSize: "12px", color: "#1c97e3", letterSpacing: "0.3px" }}>{code}</Typography>
      )}
      <TruncatedText
        onlyWhenTruncated
        text={name || "-"}
        maxWidth={200}
        sx={{ fontWeight: 600, fontSize: "13px", color: "#1a1a1a", lineHeight: 1.3 }}
      />
    </Box>
  </TableCell>
);


const DocumentsList = () => {
  const navigate = useNavigate();
  const { showSnackbar } = useSnackbar();
  const changeableItems = useChangeableItems();
  const getLabel = (itemKey) => getLabelByKey(changeableItems, itemKey);
  const getPluralLabel = (itemKey) => getPluralLabelByKey(changeableItems, itemKey);

  const [documents, setDocuments] = useState([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [searchKey, setSearchKey] = useState("");
  const debouncedSearch = useDebounce(searchKey).trim();
  const [page, setPage] = useState(0);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [showTable, setShowTable] = useState(false);

  // Filters (same Drawer flow as the Question Bank list): Subject only lists the selected Program's subjects
  const [openDrawer, setOpenDrawer] = useState(false);
  const [programOptions, setProgramOptions] = useState([]);
  const [subjectOptions, setSubjectOptions] = useState([]);
  const [selectedProgram, setSelectedProgram] = useState(null);
  const [selectedSubject, setSelectedSubject] = useState(null);
  const [appliedProgram, setAppliedProgram] = useState(null);
  const [appliedSubject, setAppliedSubject] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [appliedStatus, setAppliedStatus] = useState(null);

  useEffect(() => {
    const timer = setTimeout(() => setShowTable(true), 100);
    return () => clearTimeout(timer);
  }, []);

  // Programs for the filter — same role-aware dropdown as the Upload page
  useEffect(() => {
    if (!openDrawer || programOptions.length) return;
    api
      .post("GetAssignedProgramsDropdown", {})
      .then((res) => {
        if (res?.data?.status === "success") setProgramOptions(res.data.data || []);
      })
      .catch((error) => console.log("Error fetching programs:", error));
  }, [openDrawer, programOptions.length]);

  // Subjects of the selected Program only
  useEffect(() => {
    if (!selectedProgram?.program_id) {
      setSubjectOptions([]);
      return;
    }
    let ignore = false;
    api
      .post("GetAssignedCategoriesDropdown", { program_id: selectedProgram.program_id })
      .then((res) => {
        if (!ignore && res?.data?.status === "success") setSubjectOptions(res.data.data || []);
      })
      .catch((error) => console.log("Error fetching subjects:", error));
    return () => {
      ignore = true;
    };
  }, [selectedProgram]);

  // Same paging payload as the other lists (page, size, search_key) + the applied filters
  useEffect(() => {
    let ignore = false;
    const fetchDocuments = async () => {
      try {
        setLoading(true);
        const res = await api.post("/documents/list", {
          page,
          size: ROWS_PER_PAGE,
          search_key: debouncedSearch,
          ...(appliedProgram?.program_id ? { program_id: appliedProgram.program_id } : {}),
          ...(subjectId(appliedSubject) ? { category_id: subjectId(appliedSubject) } : {}),
          ...(appliedStatus ? { status: appliedStatus } : {}),
        });
        if (ignore) return;
        if (res?.data?.status === "success") {
          const data = res.data.data || [];
          setDocuments(data);
          setCount(res.data.total ?? res.data.count ?? data.length);
        } else {
          showSnackbar("error", res?.data?.message || "Failed to load documents.");
        }
      } catch (error) {
        console.log("Error fetching documents:", error);
        if (!ignore && error?.response?.status !== 401) {
          showSnackbar("error", error?.response?.data?.message || "Failed to load documents. Please try again.");
        }
      } finally {
        if (!ignore) setLoading(false);
      }
    };
    fetchDocuments();
    return () => {
      ignore = true;
    };
  }, [page, debouncedSearch, appliedProgram, appliedSubject, appliedStatus, showSnackbar]);

  // Until the API pages and searches on its side it returns every document — page and search them here
  const serverPaged = documents.length <= ROWS_PER_PAGE;
  const search = debouncedSearch.toLowerCase();
  const filtered = serverPaged
    ? documents
    : documents.filter((item) =>
        [item.document_name, item.category_name, item.program_name]
          .filter(Boolean)
          .some((value) => value.toLowerCase().includes(search))
      );
  const listData = serverPaged ? documents : filtered.slice(page * ROWS_PER_PAGE, page * ROWS_PER_PAGE + ROWS_PER_PAGE);
  const totalCount = serverPaged ? count : filtered.length;

  const headings = [
    { label: "S.No", align: "center" },
    { label: `${getLabel("PROGRAM_CODE")}/${getLabel("PROGRAMS")}`, align: "left" },
    { label: `${getLabel("SUBJECT_CODE")}/${getLabel("SUBJECT")}`, align: "left" },
    { label: "Document Name", align: "left" },
    { label: "Status", align: "center" },
    { label: "Actions", align: "center" },
  ];

  const handleSearch = (e) => {
    setPage(0);
    setSearchKey(e.target.value);
  };

  const handleApplyFilters = () => {
    setPage(0);
    setAppliedProgram(selectedProgram);
    setAppliedSubject(selectedSubject);
    setAppliedStatus(selectedStatus);
    setOpenDrawer(false);
  };

  const handleClickClear = () => {
    setPage(0);
    setSelectedProgram(null);
    setSelectedSubject(null);
    setSelectedStatus(null);
    setAppliedProgram(null);
    setAppliedSubject(null);
    setAppliedStatus(null);
  };

  // Removing the Program chip also removes its Subject (subjects belong to the program)
  const clearProgramFilter = () => {
    setPage(0);
    setSelectedProgram(null);
    setSelectedSubject(null);
    setAppliedProgram(null);
    setAppliedSubject(null);
  };

  const clearStatusFilter = () => {
    setPage(0);
    setSelectedStatus(null);
    setAppliedStatus(null);
  };

  const clearSubjectFilter = () => {
    setPage(0);
    setSelectedSubject(null);
    setAppliedSubject(null);
  };

  // Edit needs the full details (ids, document_key, file_name) — the list row only has names
  const fetchDocumentDetails = async (item) => {
    try {
      setDetailsLoading(true);
      const res = await api.post("/documents/view", viewPayload(item));
      if (res?.data?.status === "success") return res.data.data;
      showSnackbar("error", res?.data?.message || "Failed to load the document details.");
    } catch (error) {
      console.log("Error fetching document details:", error);
      if (error?.response?.status !== 401) {
        showSnackbar("error", error?.response?.data?.message || "Failed to load the document details. Please try again.");
      }
    } finally {
      setDetailsLoading(false);
    }
    return null;
  };

  const handleView = (item) =>
    navigate("/documents/view", { state: { document_key: item.document_key, document_id: item.document_id } });

  const handleUpload = () => navigate("/documents/add");
  const openEdit = (document) => navigate("/documents/add", { state: { document } });
  const handleEdit = async (item) => {
    const details = await fetchDocumentDetails(item);
    if (details) openEdit(details);
  };

  return (
    <Box sx={{ minHeight: "100vh", p: 1 }}>
      {detailsLoading && <OverAllLoader message="Fetching document details..." />}
      <Drawer
        anchor="right"
        open={openDrawer}
        onClose={() => setOpenDrawer(false)}
        PaperProps={{
          sx: {
            width: 320,
            p: 3,
            display: "flex",
            flexDirection: "column",
            gap: 2.5,
            height: "100vh",
            borderRadius: "12px 0 0 12px",
          },
        }}
      >
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center" gap={1}>
            <FilterListIcon color="primary" />
            <Typography variant="h6" fontWeight={600} color="primary">
              Filters
            </Typography>
          </Box>
          <IconButton aria-label="Close" size="small" onClick={() => setOpenDrawer(false)}>
            <CloseIcon />
          </IconButton>
        </Box>

        <Divider />

        <Box display="flex" flexDirection="column" gap={0.8}>
          <Typography variant="subtitle2" fontWeight={600}>
            {getLabel("PROGRAMS")}
          </Typography>
          <Autocomplete
            size="small"
            options={programOptions}
            value={selectedProgram}
            onChange={(event, newValue) => {
              setSelectedProgram(newValue);
              // A Subject picked under the previous program no longer applies
              setSelectedSubject(null);
            }}
            getOptionLabel={programLabel}
            isOptionEqualToValue={(option, value) => option?.program_id === value?.program_id}
            renderOption={(props, option) => (
              <li {...props} key={option.program_id}>
                {programLabel(option)}
              </li>
            )}
            renderInput={(params) => (
              <TextField {...params} placeholder={`All ${getPluralLabel("PROGRAMS")}`} fullWidth variant="outlined" />
            )}
          />
        </Box>

        <Box display="flex" flexDirection="column" gap={0.8}>
          <Typography variant="subtitle2" fontWeight={600}>
            {getLabel("SUBJECT")}
          </Typography>
          <Autocomplete
            size="small"
            options={subjectOptions}
            disabled={!selectedProgram}
            value={selectedSubject}
            onChange={(event, newValue) => setSelectedSubject(newValue)}
            getOptionLabel={subjectLabel}
            isOptionEqualToValue={(option, value) => subjectId(option) === subjectId(value)}
            renderOption={(props, option) => (
              <li {...props} key={subjectId(option)}>
                {subjectLabel(option)}
              </li>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder={selectedProgram ? `All ${getPluralLabel("SUBJECT")}` : `Select ${getLabel("PROGRAMS")} first`}
                fullWidth
                variant="outlined"
              />
            )}
          />
        </Box>

        <Box display="flex" flexDirection="column" gap={0.8}>
          <Typography variant="subtitle2" fontWeight={600}>
            Status
          </Typography>
          <Autocomplete
            size="small"
            options={STATUSES}
            getOptionLabel={formatStatus}
            value={selectedStatus}
            onChange={(event, newValue) => setSelectedStatus(newValue)}
            renderInput={(params) => <TextField {...params} placeholder="All Statuses" fullWidth variant="outlined" />}
          />
        </Box>

        <Button variant="contained" color="primary" fullWidth className="customButton" onClick={handleApplyFilters} sx={{ mt: 2 }}>
          Apply Filters
        </Button>
      </Drawer>

      {/* Header: title + search + Upload Document */}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 2, pb: 2, mb: 2 }}>
        <Grid sx={{ display: "flex", alignItems: "center" }}>
          <Box className="icon-box">
            <FolderOpenOutlinedIcon className="iconHeading" />
          </Box>
          <Box sx={{ ml: 2 }}>
            <Typography sx={{ fontSize: { xs: 20, sm: 28 }, fontWeight: 700, color: "#0a0a0a" }}>Documents</Typography>
          </Box>
        </Grid>
        <Grid sx={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
          <TextField
            value={searchKey}
            size="small"
            variant="outlined"
            placeholder="Search Document"
            onChange={handleSearch}
            className="custom-search-field"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
          <Button
            variant="contained"
            className="gradient-button"
            onClick={handleUpload}
            startIcon={<CloudUploadOutlinedIcon sx={{ color: "white" }} />}
          >
            <Typography sx={{ color: "white" }}>Upload Document</Typography>
          </Button>
        </Grid>
      </Box>

      <TableContainer
        component={Paper}
        sx={{
          opacity: showTable ? 1 : 0,
          transform: showTable ? "translateY(0)" : "translateY(40px)",
          transition: "opacity 0.5s ease, transform 0.5s ease",
        }}
      >
        <Table>
          <TableHead>
            <TableRow>
              <TableCell colSpan={headings.length}>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%" }}>
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, flex: 1, minWidth: 0 }}>
                    {appliedProgram && (
                      <Box className="filter-chip">
                        <Typography sx={{ fontSize: "13px", fontWeight: 500, overflowWrap: "anywhere" }}>{programLabel(appliedProgram)}</Typography>
                        <IconButton size="small" onClick={clearProgramFilter} sx={{ ml: 0.5 }}>
                          <CloseIcon sx={{ fontSize: "14px" }} />
                        </IconButton>
                      </Box>
                    )}
                    {appliedSubject && (
                      <Box className="filter-chip">
                        <Typography sx={{ fontSize: "13px", fontWeight: 500, overflowWrap: "anywhere" }}>{subjectLabel(appliedSubject)}</Typography>
                        <IconButton size="small" onClick={clearSubjectFilter} sx={{ ml: 0.5 }}>
                          <CloseIcon sx={{ fontSize: "14px" }} />
                        </IconButton>
                      </Box>
                    )}
                    {appliedStatus && (
                      <Box className="filter-chip">
                        <Typography sx={{ fontSize: "13px", fontWeight: 500 }}>{formatStatus(appliedStatus)}</Typography>
                        <IconButton size="small" onClick={clearStatusFilter} sx={{ ml: 0.5 }}>
                          <CloseIcon sx={{ fontSize: "14px" }} />
                        </IconButton>
                      </Box>
                    )}
                  </Box>

                  <Box sx={{ display: "flex", gap: 1, flexShrink: 0, ml: 2 }}>
                    <IconButton onClick={() => setOpenDrawer(true)} color="primary" className="more-filter-btn">
                      <FilterListIcon />
                      <Typography className="more-filter-text">More filter</Typography>
                    </IconButton>

                    {(appliedProgram || appliedSubject || appliedStatus) && (
                      <Button variant="outlined" className="clear-filter-btn" onClick={handleClickClear}>
                        Clear Filter
                      </Button>
                    )}
                  </Box>
                </Box>
              </TableCell>
            </TableRow>
            <TableRow>
              {headings.map((heading) => (
                <TableCell key={heading.label} align={heading.align} className="tableHeader">
                  <b>{heading.label}</b>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody className="tablebody">
            {loading ? (
              <TableRow>
                <TableCell colSpan={headings.length} align="center" sx={{ py: 10 }}>
                  <TableLoader message="Fetching documents..." />
                </TableCell>
              </TableRow>
            ) : listData.length === 0 ? (
              <TableRow>
                <TableCell colSpan={headings.length} align="center" sx={{ py: 10 }}>
                  <TableNodata />
                </TableCell>
              </TableRow>
            ) : (
              <>
                {listData.map((item, index) => (
                  <TableRow
                    key={item.document_id}
                    hover
                    sx={{
                      backgroundColor: index % 2 === 1 ? "#FAFAFA" : "inherit",
                      "&:hover": { backgroundColor: "#f1f1f1" },
                    }}
                  >
                    <TableCell align="center">{page * ROWS_PER_PAGE + index + 1}</TableCell>
                    <CodeNameCell code={item.program_code} name={item.program_name} />
                    <CodeNameCell code={item.category_code} name={item.category_name} />
                    <TableCell>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <PictureAsPdfOutlinedIcon sx={{ color: "#e53935", fontSize: "20px", flexShrink: 0 }} />
                        <TruncatedText
                          onlyWhenTruncated
                          text={item.document_name || "-"}
                          maxWidth={200}
                          sx={{ fontWeight: 600, fontSize: "13px", color: "#1a1a1a" }}
                        />
                      </Box>
                    </TableCell>
                    <TableCell align="center" sx={{ whiteSpace: "nowrap", width: "1%" }}>
                      <StatusBadge status={item.status} />
                    </TableCell>
                    <TableCell align="center" sx={{ padding: "10px 10px", whiteSpace: "nowrap", width: "1%" }}>
                      <Box display="flex" justifyContent="center" alignItems="center" gap={2}>
                        <IconButton size="small" onClick={() => handleView(item)} sx={{ display: "flex", gap: 0.5, p: 0 }}>
                          <VisibilityIcon color="primary" />
                          <Typography color="primary" sx={{ fontSize: "12px" }}>
                            View
                          </Typography>
                        </IconButton>
                        <IconButton size="small" onClick={() => handleEdit(item)} sx={{ display: "flex", gap: 0.5, p: 0 }}>
                          <EditIcon color="primary" />
                          <Typography color="primary" sx={{ fontSize: "12px" }}>
                            Edit
                          </Typography>
                        </IconButton>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
                {((page === 0 && totalCount > ROWS_PER_PAGE) || page !== 0) && (
                  <CustomPagination
                    page={page}
                    setPage={setPage}
                    count={totalCount}
                    rowsPerPage={ROWS_PER_PAGE}
                    colSpan={headings.length}
                  />
                )}
              </>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default DocumentsList;
